/* oxlint-disable react/jsx-props-no-spreading */
import { Page, PageStatus } from 'corso-admin-ui';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { PricingSet } from '~/components/PricingSet';
import { usePathParams } from '~/hooks/usePathParams';
import { useProtectSetById } from '~/hooks/useProtectSets';

export default function PricingSetPage() {
  const { setId } = usePathParams(
    z.object({ setId: z.coerce.number().optional() }),
  );

  const query = useProtectSetById(setId);

  const defaultPageProps = {
    size: 'fullWidth' as const,
    title: 'Pricing Set',
    backAction: {
      to: '/pricing-sets',
      accessibilityLabel: 'Back to pricing sets',
    },
  };

  return match(query)
    .with({ status: 'pending' }, () => (
      <Page {...defaultPageProps}>
        <PageStatus.Loading title="Loading pricing set" />
      </Page>
    ))
    .with({ status: 'error' }, () => (
      <Page {...defaultPageProps}>
        <PageStatus.Error
          title="Failed to load pricing set"
          error={query.error}
        />
      </Page>
    ))
    .with({ status: 'success', data: P.select() }, (detail) => (
      <Page
        size="fullWidth"
        title={detail.name}
        backAction={{
          to: '/pricing-sets',
          accessibilityLabel: 'Back to pricing sets',
        }}
        primaryAction={{
          icon: 'download',
          content: 'Download CSV',
          disabled: detail.units.length === 0,
          onAction: () => {
            const csv = [
              [
                'Charge Amount (USD)',
                'Cart Value Start (USD)',
                'Cart Value End (USD)',
              ],
              ...detail.units.map((unit) => [
                unit.chargeAmount.toFixed(2),
                unit.rangeStart.toFixed(2),
                unit.rangeEnd.toFixed(2),
              ]),
            ]
              .map((row) => row.join(','))
              .join('\n');

            const url = URL.createObjectURL(
              new Blob([csv], { type: 'text/csv' }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.download = `pricing-set-${detail.id}-export.csv`;
            link.click();
            URL.revokeObjectURL(url);
          },
        }}
      >
        <PricingSet detail={detail} />
      </Page>
    ))
    .exhaustive();
}
